import { useQueries, useQuery } from '@tanstack/react-query';
import type { ModelId } from '@/shared/types/common.types';
import { workspaceService } from '../services/workspaceService';
import type { WorkspaceProject } from '../types/workspace.types';
import { workspaceKeys } from './workspaceKeys';

/** How many of the caller's projects are searched for a model's owner. */
const OWNER_SEARCH_LIMIT = 50;

export type ModelOwner =
  | { readonly state: 'pending' }
  | { readonly state: 'found'; readonly project: WorkspaceProject }
  | { readonly state: 'none' };

/**
 * The workspace project a model belongs to.
 *
 * A project lists its models, but a model does not name its project, and Core's project list
 * carries no model ids. So the owner is found by reading the caller's projects until one links
 * the model. That is the price of an old `/projects/:id` link; the app's own links already
 * name the project, so this runs only for bookmarks and for pages that only know the model.
 */
export function useModelOwner(modelId: ModelId | null, enabled: boolean): ModelOwner {
  const active = enabled && modelId !== null;

  const list = useQuery({
    queryKey: ['workspace', 'model-owner', 'projects'],
    queryFn: () => workspaceService.listProjects({ limit: OWNER_SEARCH_LIMIT }),
    enabled: active,
  });

  const projects = useQueries({
    queries: (list.data?.items ?? []).map((summary) => ({
      queryKey: workspaceKeys.project(summary.id),
      queryFn: () => workspaceService.getProject(summary.id),
      enabled: active,
    })),
  });

  if (!active) return { state: 'none' };

  const owner = projects.find(
    (query) => query.data?.modelIds.some((id) => Number(id) === Number(modelId)) === true
  )?.data;
  if (owner !== undefined) return { state: 'found', project: owner };

  const waiting = list.isPending || projects.some((query) => query.isPending);
  return waiting && !list.isError ? { state: 'pending' } : { state: 'none' };
}
